import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminApi } from '@/services/api';
import { Save, Settings as SettingsIcon, ArrowLeft } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import '@/styles/AdminSettings.css';

const AdminSettings = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    if (!adminApi.isAuthenticated()) {
      navigate('/admin/login');
      return;
    }
    loadSettings();
  }, [navigate]);

  const loadSettings = async () => {
    try {
      const data = await adminApi.getSettings();
      if (!data) {
        // Set default values
        setSettings({
          id: 'personal_settings',
          name: 'Ahmad Kassabieh',
          title: 'Senior Mobile Engineer',
          subtitle: 'Flutter • iOS • Fintech Specialist',
          tagline: 'Building scalable mobile solutions for 30M+ users across MENA & Africa',
          bio: 'Senior Mobile Engineer specializing in Flutter and iOS...',
          location: 'Beirut, Lebanon',
          email: 'ahmadkassabieh@gmail.com',
          phone: '+96171215047',
          linkedin: 'https://www.linkedin.com/in/ahmad-kassabieh',
          github: 'https://github.com/AhmadKsb',
          profileImage: '',
          heroBackground: '',
          aboutImage: '',
          stats: { years: '6+', users: '30M+', projects: '50+', team: '10+' }
        });
      } else {
        setSettings(data);
      }
    } catch (error) {
      console.error('Failed to load settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setSettings({ ...settings, [field]: value });
  };

  const handleStatChange = (key, value) => {
    setSettings({
      ...settings,
      stats: { ...settings.stats, [key]: value }
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await adminApi.updateSettings(settings);
      toast({ title: "Settings saved successfully!" });
    } catch (error) {
      toast({ title: "Failed to save settings", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  if (loading || !settings) return <div className="admin-loading">Loading...</div>;

  return (
    <div className="admin-dashboard">
      <aside className="admin-sidebar">
        <div className="sidebar-header"><h2>AK Admin</h2></div>
        <nav className="sidebar-nav">
          <button className="nav-item" onClick={() => navigate('/admin/dashboard')}>Dashboard</button>
          <button className="nav-item" onClick={() => navigate('/admin/contacts')}>Messages</button>
          <button className="nav-item" onClick={() => navigate('/admin/projects')}>Projects</button>
          <button className="nav-item" onClick={() => navigate('/admin/articles')}>Articles</button>
          <button className="nav-item" onClick={() => navigate('/admin/experience')}>Experience</button>
          <button className="nav-item active"><SettingsIcon size={20} />Settings</button>
        </nav>
      </aside>

      <main className="admin-content">
        <div className="content-header">
          <button className="back-btn" onClick={() => navigate('/admin/dashboard')}>
            <ArrowLeft size={20} />Back to Dashboard
          </button>
          <h1>Personal Settings</h1>
        </div>

        <div className="settings-container">
          <div className="settings-section">
            <h3>Basic Information</h3>
            <div className="form-grid">
              <div className="form-group">
                <label>Full Name</label>
                <input value={settings.name} onChange={(e) => handleChange('name', e.target.value)} />
              </div>
              <div className="form-group">
                <label>Title</label>
                <input value={settings.title} onChange={(e) => handleChange('title', e.target.value)} />
              </div>
              <div className="form-group full-width">
                <label>Subtitle (e.g., Flutter • iOS • Fintech)</label>
                <input value={settings.subtitle} onChange={(e) => handleChange('subtitle', e.target.value)} />
              </div>
              <div className="form-group full-width">
                <label>Tagline</label>
                <input value={settings.tagline} onChange={(e) => handleChange('tagline', e.target.value)} />
              </div>
              <div className="form-group full-width">
                <label>Bio</label>
                <textarea rows="4" value={settings.bio} onChange={(e) => handleChange('bio', e.target.value)} />
              </div>
            </div>
          </div>

          <div className="settings-section">
            <h3>Contact Information</h3>
            <div className="form-grid">
              <div className="form-group">
                <label>Email</label>
                <input value={settings.email} onChange={(e) => handleChange('email', e.target.value)} />
              </div>
              <div className="form-group">
                <label>Phone</label>
                <input value={settings.phone} onChange={(e) => handleChange('phone', e.target.value)} />
              </div>
              <div className="form-group">
                <label>Location</label>
                <input value={settings.location} onChange={(e) => handleChange('location', e.target.value)} />
              </div>
              <div className="form-group">
                <label>LinkedIn URL</label>
                <input value={settings.linkedin} onChange={(e) => handleChange('linkedin', e.target.value)} />
              </div>
              <div className="form-group">
                <label>GitHub URL</label>
                <input value={settings.github} onChange={(e) => handleChange('github', e.target.value)} />
              </div>
            </div>
          </div>

          <div className="settings-section">
            <h3>Statistics (Hero Section)</h3>
            <div className="form-grid">
              <div className="form-group">
                <label>Years Experience</label>
                <input value={settings.stats?.years || ''} onChange={(e) => handleStatChange('years', e.target.value)} />
              </div>
              <div className="form-group">
                <label>Users Served</label>
                <input value={settings.stats?.users || ''} onChange={(e) => handleStatChange('users', e.target.value)} />
              </div>
              <div className="form-group">
                <label>Projects Completed</label>
                <input value={settings.stats?.projects || ''} onChange={(e) => handleStatChange('projects', e.target.value)} />
              </div>
              <div className="form-group">
                <label>Team Members Led</label>
                <input value={settings.stats?.team || ''} onChange={(e) => handleStatChange('team', e.target.value)} />
              </div>
            </div>
          </div>

          <div className="settings-section">
            <h3>Images (Optional - URLs)</h3>
            <div className="form-grid">
              <div className="form-group full-width">
                <label>Profile Image URL</label>
                <input value={settings.profileImage} onChange={(e) => handleChange('profileImage', e.target.value)} placeholder="https://..." />
              </div>
              <div className="form-group full-width">
                <label>Hero Background Image URL</label>
                <input value={settings.heroBackground} onChange={(e) => handleChange('heroBackground', e.target.value)} placeholder="https://..." />
              </div>
              <div className="form-group full-width">
                <label>About Section Image URL</label>
                <input value={settings.aboutImage} onChange={(e) => handleChange('aboutImage', e.target.value)} placeholder="https://..." />
              </div>
            </div>
          </div>

          <button className="save-btn" onClick={handleSave} disabled={saving}>
            <Save size={20} />
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </main>
    </div>
  );
};

export default AdminSettings;